import { useState, useEffect, useRef } from 'react';
import {
  Button,
  ButtonWidth,
  CardRadioButton,
  Drawer,
  MLPVersion,
  NextgenImage,
  RadioButtonAlignment,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import { formatPrice } from '@nextgen-web-framework/core';
import {
  FilterOption,
  PaymentApi,
  PaymentOption,
  PmpProjection,
  InstallmentPlan,
  PlanDetails,
  InstallmentPlanType,
  IppPlanCMSResponseType,
} from '@commerce-payment-webcomponents/sdk';
import { cmsApi } from '@commerce-webcomponents/sdk';
import { ProductType } from '../../../instalment-details/components/types/enums';
import { PaymentLoader, PaymentLoaderType } from '../../../payment-loader/PaymentLoader';
import styles from './InstalmentPlansDrawer.module.scss';
import InstalmentPlansDrawerProps from './types/propTypes';

export const InstalmentPlansDrawer = ({
  onDrawerClose,
  onConfirmCallBack,
  orderTotal,
  orderType,
  selectedPlanId,
  productDetails,
  enablePayInFull,
  filterOption,
  updateLabel,
}: InstalmentPlansDrawerProps) => {
  const t = useTranslation();
  const [availablePlans, setAvailablePlans] = useState<InstallmentPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState(selectedPlanId);
  const [cmsResponsePlanInfo, setCmsResponsePlanInfo] = useState<IppPlanCMSResponseType[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const paymentMethodCodeRef = useRef('');
  const tempKeyMapping = {
    true: 1,
    false: 0,
    undefined: 0,
  };

  useEffect(() => {
    //Make an Api call  100186TH  109745TH
    const productMap: {
      type: string;
      id?: string;
      quantity: number;
      ippflag: boolean;
      value?: number;
      isPromotionalProduct?: boolean;
    }[] = [];
    productDetails?.forEach((product) => {
      productMap.push({
        type: ProductType.VARIANT,
        id: product.productId,
        quantity: product.quantity,
        ippflag: product.ippFlag,
        value: product?.discountedPrice,
        isPromotionalProduct: product?.isPromotionalProduct,
      });
    });
    const localFilterOption = {
      product: productMap,
      orderTotal: orderTotal,
      orderType: orderType,
      retry: filterOption?.retry || false,
      orderId: filterOption?.orderId,
      awaitingPayment: filterOption?.awaitingPayment,
      enablePayInFull: enablePayInFull,
    };
    const fetchPaymentOption = async () => {
      const paymentOptions: PaymentOption[] = await PaymentApi.getPaymentOptions(
        localFilterOption as FilterOption,
        PmpProjection.Ipp,
      );
      paymentMethodCodeRef.current = paymentOptions?.[0]?.paymentMethod?.code as string;
      const plans = paymentOptions[0]?.additionalInfo?.plans;
      setByDefaultPlan(JSON.parse(JSON.stringify(plans)));
      if (plans?.length) {
        //making call for content stack
        const planIds: (string | undefined)[] = [];
        plans?.forEach((planObj) => planIds.push(`"${planObj.id?.toString()}"`));
        planIds.length && fetchContentStackPlans(planIds);
      } else {
        setShowLoader(false);
      }
      setAvailablePlans(plans || []);
    };
    fetchPaymentOption();
    setShowLoader(true);
  }, []);

  const fetchContentStackPlans = async (planIds: (string | undefined)[]) => {
    const cmsResponse = (await cmsApi.fetchAllContent({
      uId: 'instalment_payment_plan',
      params: [
        {
          key: 'query',
          value: `{"plan_id":{"$in":[${planIds}]}}`,
        },
      ],
    })) as IppPlanCMSResponseType[];
    if (cmsResponse) {
      setCmsResponsePlanInfo(cmsResponse);
      setShowLoader(false);
    }
  };
  const getCmsObj = (id: string) => {
    const result = cmsResponsePlanInfo?.filter((cmsObj) => cmsObj.plan_id === id);
    return result?.[0];
  };

  const setByDefaultPlan = (plans: PlanDetails[]) => {
    if (!selectedPlanId) {
      plans.forEach((plan) => {
        if (plan?.planType === InstallmentPlanType.Full) {
          setSelectedPlan(plan?.id || '');
        }
      });
    }
  };

  const onRadioChange = (event: { target: { id: string } }) => {
    setSelectedPlan(event?.target?.id);
  };

  const onConfirmHandler = () => {
    onDrawerClose();
    const planTitle = getCmsObj(selectedPlan)?.plan_title || '';
    onConfirmCallBack(selectedPlan, planTitle, paymentMethodCodeRef.current);
    updateLabel?.(planTitle);
  };
  const footer = (
    <Button
      data-testId="confirm-ipp-cta"
      title={t('payment.creditCard.confirmBtn')}
      buttonWidth={ButtonWidth.FULL_WIDTH}
      onClick={onConfirmHandler}
      disabled={!selectedPlan}
    />
  );

  return (
    <Drawer
      header={t('payment.instalmentDetails.cta.label')}
      onClose={onDrawerClose}
      closeIcon
      className={styles['instalment-plans-drawer-body']}
      footer={footer}
      bottomGap={0}
      closeOnBackdropClick
    >
      <>
        <div className={styles['instalment-items']}>
          {productDetails?.length} {productDetails?.length > 1 ? 'items' : 'item'}{' '}
          {t('payment.instalmentPlanDrawer.withInstalment')}
        </div>
        <div className={styles['product-details-container']}>
          {productDetails?.map((item) => (
            <div key={item.productId} className={styles['product-details-wrapper']}>
              <div className={styles['card-image']}>
                <NextgenImage src={item.image} width="54" height="54" alt="product" />
              </div>
              <div className={styles['card-details']}>
                <div className={styles['card-description']}>
                  <div
                    className={styles['left-panel']}
                    role="button"
                    tabIndex={0}
                    data-testid="card-description-button"
                    aria-label={'productName'}
                  >
                    {item?.brandName && <div className={styles['brand-name']}>{item.brandName}</div>}
                    {item?.name && <div className={styles['product-name']}>{item.name}</div>}
                  </div>
                  <div className={styles['product-right-panel']}>
                    <div className={styles['product-price']}>
                      {formatPrice(Number(item?.actualPrice), orderTotal?.currency)}
                    </div>

                    <div className={styles['product-quanity']}>
                      {t('payment.instalmentPlanDrawer.quantity')} {item.quantity}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles['plans-title']}>{t('payment.instalmentPlanDrawer.title')}</div>

        {showLoader ? (
          <PaymentLoader loaderType={PaymentLoaderType.INSTALMENT_DETAILS} />
        ) : (
          <div className={styles['plans-card']}>
            {availablePlans
              ?.sort(
                (a, b) => tempKeyMapping[a?.softFilter as keyof object] - tempKeyMapping[b?.softFilter as keyof object],
              )
              ?.map((plan) => (
                <CardRadioButton
                  key={plan?.id}
                  checked={selectedPlan === plan.id}
                  cardDescription={
                    plan?.planType !== InstallmentPlanType.Full ? t('payment.instalmentDetails.tenureMsg') : ''
                  }
                  className={selectedPlan === plan.id ? styles['plans-card-radio'] : ''}
                  id={plan.id}
                  label={getCmsObj(plan?.id || '')?.plan_title}
                  mlpVersion={MLPVersion.ONE}
                  onChange={onRadioChange}
                  radioButtonAlignment={RadioButtonAlignment.CENTER}
                  disabled={plan.softFilter}
                  theme={selectedPlan === plan.id ? Theme.PURPLE : Theme.BLACK}
                  data-testid="plans-rendered"
                />
              ))}
          </div>
        )}
      </>
    </Drawer>
  );
};
